import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { HelpContentService } from '../help-content.service';
import { HelpSearchService, HelpSearchHit } from '../help-search.service';
import { HelpSearchMissService } from '../help-search-miss.service';
import { HelpArticle, HelpCategory } from '../../../core/models/help-content.model';
import { SeoService } from '../../../core/services/seo.service';
import { KontaktformularComponent } from '../kontaktformular/kontaktformular.component';

type AudienceKey = 'kunden' | 'handwerker';

@Component({
  selector: 'app-hilfe-uebersicht',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, KontaktformularComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './hilfe-uebersicht.component.html',
  styleUrl: './hilfe-uebersicht.component.scss',
})
export class HilfeUebersichtComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly content = inject(HelpContentService);
  private readonly search = inject(HelpSearchService);
  private readonly miss = inject(HelpSearchMissService);
  private readonly seo = inject(SeoService);

  readonly audience = signal<AudienceKey>('kunden');
  readonly categories = signal<HelpCategory[]>([]);
  readonly articlesByCategory = signal<Record<string, HelpArticle[]>>({});

  // Suche
  readonly query = signal('');
  readonly hits = signal<HelpSearchHit[]>([]);
  readonly isSearching = computed(() => this.query().trim().length >= 2);

  // Akkordeon-State: { categoryId: { slug: open } }
  readonly openSlug = signal<string | null>(null);

  private readonly query$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    const audience = (this.route.snapshot.data['audience'] as AudienceKey) ?? 'kunden';
    this.audience.set(audience);

    const cats = this.content.getCategories(audience);
    this.categories.set(cats);

    const grouped: Record<string, HelpArticle[]> = {};
    for (const cat of cats) {
      grouped[cat.id] = this.content.getArticlesByCategory(audience, cat.id);
    }
    this.articlesByCategory.set(grouped);

    if (audience === 'kunden') {
      this.seo.apply({
        title: 'Hilfe für Kunden',
        description:
          'Antworten zu Anfragen, Buchungen, Zahlungen, Stornierung, Mängeln und mehr. Alle wichtigen Fragen rund um deine Buchung auf Craftly.',
        path: '/hilfe/kunden',
      });
    } else {
      this.seo.apply({
        title: 'Hilfe für Handwerker',
        description:
          'Antworten zu Onboarding, Anfragen, Provision, Auszahlung, DAC7-Meldung und mehr. Alle wichtigen Fragen rund um deinen Betrieb auf Craftly.',
        path: '/hilfe/handwerker',
      });
    }

    this.query$
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe((q) => this.runSearch(q));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQueryChange(q: string) {
    this.query.set(q);
    this.query$.next(q);
  }

  toggleArticle(slug: string) {
    this.openSlug.update((open) => (open === slug ? null : slug));
  }

  scrollToCategory(id: string) {
    const el = document.getElementById('cat-' + id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  articleHref(slug: string): string {
    return `/hilfe/${this.audience()}/${slug}`;
  }

  private runSearch(q: string) {
    const hits = this.search.search(this.audience(), q, 8);
    this.hits.set(hits);
    if (q.trim().length >= 3 && hits.length === 0) {
      this.miss.record(this.audienceParam(), q);
    }
  }

  private audienceParam() {
    return this.content.audienceParam(this.audience());
  }

  /** Used by the contact form to scope topic options. */
  contactAudience(): 'kunde' | 'handwerker' {
    return this.audienceParam();
  }
}
